import React, { useState } from 'react';
import { Grid3x3, Layers, Bath, Home, Trees, Building2, Check, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

const NewServices = () => {
  const [selectedService, setSelectedService] = useState(null);

  const services = [
    {
      icon: Grid3x3,
      title: 'Floor Tiling',
      description: 'Professional floor tiling for residential and commercial spaces with precision installation.',
      image: 'https://images.unsplash.com/flagged/photo-1556438758-872c68902f60?w=800&q=80&auto=format&fit=crop',
      details: {
        overview: 'Expert floor tiling services that transform any space with durable, beautiful results. We handle everything from residential rooms to large commercial areas.',
        features: [
          'Ceramic and porcelain tile installation',
          'Precision leveling and layout',
          'Underfloor heating compatible',
          'Anti-slip surface options',
          'Large format tile expertise',
          'Commercial grade durability',
          'Pattern and design consultation',
          'Professional grouting and sealing'
        ]
      }
    },
    {
      icon: Layers,
      title: 'Wall Tiling',
      description: 'Transform your walls with expert tiling solutions for lasting beauty and durability.',
      image: 'https://images.unsplash.com/photo-1676191099600-144cd9ee67d9?w=800&q=80&auto=format&fit=crop',
      details: {
        overview: 'Create stunning feature walls or practical splash-back areas with our professional wall tiling services.',
        features: [
          'Feature wall installations',
          'Backsplash tiling',
          'Laundry and utility areas',
          'Wet area waterproofing',
          'Tile trim and edging',
          'Custom patterns available',
          'Height and layout planning',
          'Quality finishing touches'
        ]
      }
    },
    {
      icon: Bath,
      title: 'Bathroom Tiling',
      description: 'Complete bathroom tiling services including waterproofing and modern designs.',
      image: 'https://images.unsplash.com/photo-1580398562556-d33329a0f29b?w=800&q=80&auto=format&fit=crop',
      details: {
        overview: 'Transform your bathroom into a beautiful, functional space with our comprehensive tiling services including professional waterproofing.',
        features: [
          'Full bathroom renovations',
          'Certified waterproofing',
          'Shower and bath surrounds',
          'Floor and wall tiling',
          'Modern tile selections',
          'Niche and shelf installations',
          'Anti-slip floor options',
          'Expert grouting and sealing'
        ]
      }
    },
    {
      icon: Home,
      title: 'Kitchen Tiling',
      description: 'Beautiful kitchen backsplashes and floor tiling that combines style with function.',
      image: 'https://images.unsplash.com/photo-1507089947368-19c1da9775ae?w=800&q=80&auto=format&fit=crop',
      details: {
        overview: 'Enhance your kitchen with stunning backsplashes and durable floor tiling that withstands daily use while looking amazing.',
        features: [
          'Backsplash installations',
          'Subway and mosaic tiles',
          'Kitchen floor tiling',
          'Heat-resistant materials',
          'Easy-clean surfaces',
          'Outlet and fixture integration',
          'Color matching service',
          'Splashback protection'
        ]
      }
    },
    {
      icon: Trees,
      title: 'Outdoor Tiling',
      description: 'Weather-resistant outdoor tiling for patios, balconies, and pool areas.',
      image: 'https://images.unsplash.com/photo-1767022062386-36d4af393776?w=800&q=80&auto=format&fit=crop',
      details: {
        overview: 'Create beautiful outdoor living spaces with weather-resistant tiling designed to withstand Australian conditions.',
        features: [
          'Patio and deck tiling',
          'Pool surrounds and coping',
          'Balcony waterproofing',
          'Outdoor kitchen areas',
          'UV and frost resistant',
          'Anti-slip outdoor ratings',
          'Proper drainage planning',
          'Weather-sealed finishes'
        ]
      }
    },
    {
      icon: Building2,
      title: 'Commercial Tiling',
      description: 'Large-scale commercial projects with efficient timelines and quality results.',
      image: 'https://images.unsplash.com/photo-1691380302079-2d70e95f27d0?w=800&q=80&auto=format&fit=crop',
      details: {
        overview: 'Professional commercial tiling for offices, retail spaces, and hospitality venues with minimal disruption to your operations.',
        features: [
          'High-traffic tile options',
          'Retail fit-outs',
          'Office installations',
          'Restaurant and cafe projects',
          'After-hours work available',
          'Fast turnaround times',
          'Project management',
          'Quality assurance checks'
        ]
      }
    },
  ];

  return (
    <section id="services" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Our Services
          </h2>
          <div className="w-24 h-1 bg-blue-600 mx-auto"></div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {services.map((service, index) => {
            const IconComponent = service.icon;
            return (
              <div
                key={index}
                onClick={() => setSelectedService(service)}
                className="group bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 cursor-pointer"
              >
                <div className="relative h-48 sm:h-56 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                  <div className="absolute bottom-4 left-4">
                    <IconComponent className="w-10 h-10 text-white" />
                  </div>
                </div>
                <div className="p-4 sm:p-6">
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3">{service.title}</h3>
                  <p className="text-sm sm:text-base text-gray-600 leading-relaxed mb-3">{service.description}</p>
                  <button className="text-blue-600 font-semibold hover:text-blue-700 transition-colors text-sm sm:text-base flex items-center gap-2">
                    Learn More
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Service Detail Modal */}
        <Dialog open={!!selectedService} onOpenChange={() => setSelectedService(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            {selectedService && (
              <>
                <DialogHeader>
                  <DialogTitle className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-3">
                    {React.createElement(selectedService.icon, { className: "w-7 h-7 sm:w-8 sm:h-8 text-blue-600" })}
                    {selectedService.title}
                  </DialogTitle>
                </DialogHeader>
                
                <div className="mt-6 space-y-6">
                  {/* Image */}
                  <div className="relative h-48 sm:h-64 rounded-lg overflow-hidden">
                    <img
                      src={selectedService.image}
                      alt={selectedService.title}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  {/* Overview */}
                  <div>
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3">Overview</h3>
                    <p className="text-sm sm:text-base text-gray-700 leading-relaxed">{selectedService.details.overview}</p>
                  </div>

                  {/* Features */}
                  <div>
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4">What We Offer</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {selectedService.details.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                          <Check className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                          <span className="text-sm sm:text-base text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* CTA */}
                  <div className="bg-blue-50 p-4 sm:p-6 rounded-lg">
                    <h4 className="text-base sm:text-lg font-bold text-gray-900 mb-2">Ready to get started?</h4>
                    <p className="text-sm sm:text-base text-gray-700 mb-4">Contact us today for a free consultation and quote for your {selectedService.title.toLowerCase()} project.</p>
                    <a href="#contact" onClick={() => setSelectedService(null)}>
                      <button className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-all duration-300 transform hover:scale-105">
                        Get a Free Quote
                      </button>
                    </a>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </section>
  );
};

export default NewServices;